import { plateFont, numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Collapse,
  Alert,
  Chip,
  Grid,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SpeedIcon from '@mui/icons-material/Speed';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CreateIcon from '@mui/icons-material/Create';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import ExtraLookupConfirmDialog from '../vehicles/ExtraLookupConfirmDialog';
import HelpButton from '../shared/HelpButton';
import CustomerVehicleChips from '../shared/CustomerVehicleChips';
import { InternalNotesAlert } from '../shared/InternalNote';
import NewCustomerForm, {
  EMPTY_NEW_CUSTOMER,
  isNewCustomerValid,
  isNewCustomerIncomplete,
} from '../shared/NewCustomerForm';
import ScheduleSlotFields from '../calendar/ScheduleSlotFields';
import CabasPreview from './CabasPreview';
import CabasEstimatePicker from '../cabas/CabasEstimatePicker';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import OemServiceDialog from './oem/OemServiceDialog';
import { useUserStore } from '../../store';
import useVehicleLookup from '../../hooks/useVehicleLookup';
import { flattenDrfError } from '../../utils/apiUtils';
import { formatPriceWithCurrency } from '../../utils/vat';
import { customerLabel } from '../../utils/customer';

function AddMaintenanceDialog({
  t,
  addCreatedMaintenance,
  open,
  handleClose,
  initialState = 'planned',
  onSwitchToAi,
  // Calendar context (all optional; without them behavior is the plain dashboard flow):
  // scheduleContext: { startDateTime, endDateTime, serviceBayId, employeeIds } — renders the
  //   scheduling section and schedules the created work order into the slot.
  // schedulingResources: { employees, serviceBays, events, unavailabilities } — required
  //   alongside scheduleContext; passed as props because the mobile calendar keeps these in
  //   local state, not in the calendar Zustand store.
  // onCreated({ maintenance, schedule, scheduleFields, warnings }) — replaces the navigation
  //   to the work order page after a successful create.
  scheduleContext = null,
  schedulingResources = null,
  onCreated = null,
}) {
  const { currentOrganization } = useUserStore();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const pdfImportEnabled = currentOrganization?.ai_work_order_import_enabled ?? false;
  const aiWorkOrderEnabled = currentOrganization?.ai_work_order_enabled ?? false;
  const oemServiceEnabled = currentOrganization?.oem_service_enabled ?? false;
  // CABAS imports create planned work orders, so the mode is not offered for quotes.
  const cabasEnabled = (currentOrganization?.has_cabas_integration ?? false) && initialState !== 'quote';

  // OEM package picked inside this dialog (UX-1): attached to the work order on save
  const [oemPickerOpen, setOemPickerOpen] = useState(false);
  const [oemSelection, setOemSelection] = useState(null);
  const [oemChargeStatus, setOemChargeStatus] = useState(null);
  const oemVehicleIdRef = useRef(null);
  const [createMode, setCreateMode] = useState('manual'); // 'manual' | 'pdf' | 'ai' | 'cabas'
  const [aiRequestText, setAiRequestText] = useState('');
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [cabasPreview, setCabasPreview] = useState(null);
  const [cabasConfirming, setCabasConfirming] = useState(false);
  const [formData, setFormData] = useState({});

  // A picked OEM package belongs to one vehicle — drop it if the vehicle changes.
  // Freshly looked-up vehicles have no id yet, so key on the plate as the fallback.
  const oemVehicleKey = formData?.vehicle?.id ?? formData?.vehicle?.registration_number ?? null;
  useEffect(() => {
    if (oemVehicleIdRef.current !== oemVehicleKey) {
      oemVehicleIdRef.current = oemVehicleKey;
      setOemSelection(null);
      setOemChargeStatus(null);
    }
  }, [oemVehicleKey]);

  // Price transparency on the banner: is this vehicle type already paid for?
  const oemTecdocId = formData?.vehicle?.tecdoc_id || null;
  useEffect(() => {
    if (!oemServiceEnabled || !oemTecdocId || !currentOrganization?.organization_id) return;
    let cancelled = false;
    axios
      .get('/api/oem/charge-status/', {
        params: { tecdoc_id: oemTecdocId, organization: currentOrganization.organization_id },
      })
      .then((resp) => {
        if (!cancelled) setOemChargeStatus(resp.data);
      })
      .catch(() => {
        // Banner falls back to the generic price note from /api/users/me/
      });
    return () => { cancelled = true; };
  }, [oemServiceEnabled, oemTecdocId, currentOrganization?.organization_id]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customerAutoFilledRef = useRef('none'); // 'none' | 'auto' | 'manual'
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerMode, setCustomerMode] = useState('existing');
  const [newCustomer, setNewCustomer] = useState({ ...EMPTY_NEW_CUSTOMER });
  const navigate = useNavigate();

  const [manualVehicleEntry, setManualVehicleEntry] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [odometerReading, setOdometerReading] = useState('');
  const [hourMeterReading, setHourMeterReading] = useState('');
  const [odometerWarning, setOdometerWarning] = useState(null);
  const [servicePackages, setServicePackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const customerSearchTimeoutRef = useRef(null);
  const odometerWarningTimeoutRef = useRef(null);

  // Editable copy of the calendar slot; initialized from scheduleContext on open
  const [scheduleFields, setScheduleFields] = useState(null);
  useEffect(() => {
    if (open) {
      setScheduleFields(scheduleContext ? { ...scheduleContext } : null);
    }
    // Init-on-open semantics: the slot is snapshotted when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Vehicle lookup flow (quota, typeahead, org-DB search, paid lookup + 402 handling)
  const vehicleLookup = useVehicleLookup({
    t,
    onVehicleData: (vehicleData, regNumber) => {
      setFormData((prev) => ({
        ...prev,
        vehicle: {
          ...prev.vehicle,
          ...vehicleData,
          registration_number: regNumber.toUpperCase(),
        },
      }));
      const hasData = vehicleData.manufacturer || vehicleData.model || vehicleData.vin;
      vehicleLookup.setVehicleLookedUp(!!hasData);
    },
    onLastCustomer: (customer) => {
      if (customerAutoFilledRef.current !== 'manual') {
        setSelectedCustomer(customer);
        customerAutoFilledRef.current = 'auto';
      }
    },
    onTrialRestricted: () => setManualVehicleEntry(true),
  });
  const {
    lookupLoading,
    lookupError,
    vehicleLookedUp,
    setVehicleLookedUp,
    showTrialInfo,
    showNonFinnishInfo,
    quotaInfo,
    fetchQuota,
    extraLookupDialogOpen,
    setExtraLookupDialogOpen,
    vehicleTypeaheadOptions,
    vehicleTypeaheadLoading,
    vehicleTypeaheadOpenRef,
    handleRegistrationInput,
    selectTypeaheadOption,
    requestLookup,
    confirmExtraLookup,
    interruptLookup,
    cancelLookup,
    resetLookup,
  } = vehicleLookup;

  // Search customers with debounce
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      try {
        const response = await axios.get('/api/customers/?page_size=50');
        const customersData = response.data.results || response.data;
        setCustomers(Array.isArray(customersData) ? customersData : []);
      } catch (err) {
        console.error('Error loading customers:', err);
      }
      return;
    }

    setCustomerSearchLoading(true);
    try {
      const response = await axios.get(`/api/customers/?search=${encodeURIComponent(query)}&typeahead=true`);
      const customersData = response.data.results || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (err) {
      console.error('Error searching customers:', err);
    } finally {
      setCustomerSearchLoading(false);
    }
  }, []);

  // Debounce customer search input
  useEffect(() => {
    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }

    customerSearchTimeoutRef.current = setTimeout(() => {
      searchCustomers(customerSearchInput);
    }, 300);

    return () => {
      if (customerSearchTimeoutRef.current) {
        clearTimeout(customerSearchTimeoutRef.current);
      }
    };
  }, [customerSearchInput, searchCustomers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersResponse, employeesResponse] = await Promise.all([
          axios.get('/api/customers/?page_size=50'),
          axios.get('/api/employees/'),
        ]);
        const customersData = customersResponse.data.results || customersResponse.data;
        setCustomers(Array.isArray(customersData) ? customersData : []);
        const employeesData = employeesResponse.data.results || employeesResponse.data;
        setEmployees(Array.isArray(employeesData) ? employeesData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (open) {
      fetchData();
      fetchQuota();
      setSelectedCustomer(null);
      customerAutoFilledRef.current = 'none';
      setSelectedEmployees([]);
      setCustomerSearchInput('');
      setSelectedPackage(null);
      axios.get('/api/service-packages/', { params: { is_active: true } })
        .then((res) => {
          const data = res.data.results || res.data;
          setServicePackages(Array.isArray(data) ? data : []);
        })
        .catch(() => setServicePackages([]));
    }
  }, [open, fetchQuota]);

  // A typeahead option was selected — free by-registration fetch only
  const handleVehicleTypeaheadSelect = useCallback(async (option) => {
    setFormData((prev) => ({
      ...prev,
      vehicle: { ...prev.vehicle, registration_number: option.registration_number },
    }));
    setManualVehicleEntry(false);
    await selectTypeaheadOption(option);
  }, [selectTypeaheadOption]);

  // Explicit vehicle lookup triggered by button click
  const handleLookupClick = useCallback(() => {
    const regNumber = formData.vehicle?.registration_number;
    if (!regNumber) return;
    requestLookup(regNumber);
  }, [formData.vehicle?.registration_number, requestLookup]);

  // Shared post-create tail for the manual, PDF and CABAS creation paths:
  // optionally schedule into the calendar slot, then hand off via onCreated
  // or navigate to the work order page.
  const finalizeCreate = useCallback(async (maintenanceData, { oemAttachFailed = false } = {}) => {
    let schedule = null;
    let scheduleFailed = false;

    if (scheduleContext && scheduleFields?.startDateTime && scheduleFields?.endDateTime && maintenanceData?.id) {
      const employeeIds = scheduleFields.employeeIds || [];
      try {
        const scheduleResponse = await axios.post(`/api/maintenances/${maintenanceData.id}/schedule/`, {
          start_time: scheduleFields.startDateTime.toISOString(),
          end_time: scheduleFields.endDateTime.toISOString(),
          service_bay_id: scheduleFields.serviceBayId || null,
          // employee_ids becomes the new slot's team and joins the work order team;
          // required for the PDF/CABAS paths where the work order is created server-side
          // without assignments. Omit entirely when none selected: the slot then copies
          // the work order team.
          ...(employeeIds.length > 0
            ? { employee_ids: employeeIds, primary_employee_id: employeeIds[0] }
            : {}),
        });
        // A freshly created work order has exactly one schedule, so schedules[0] is it.
        // Never reuse this pattern for existing work orders with prior schedules.
        schedule = scheduleResponse.data?.schedules?.[0] || null;
      } catch (scheduleError) {
        // Non-blocking: the work order exists and shows up in the calendar's
        // unscheduled sidebar; the caller surfaces the warning.
        console.error('Error scheduling the created work order:', scheduleError);
        scheduleFailed = true;
      }
    }

    if (addCreatedMaintenance) {
      addCreatedMaintenance(maintenanceData);
    }

    if (onCreated) {
      onCreated({
        maintenance: maintenanceData,
        schedule,
        scheduleFields,
        warnings: { scheduleFailed, oemAttachFailed },
      });
      handleClose();
      return;
    }

    handleClose();
    navigate(`/maintenances/${maintenanceData.id}`, {
      state: oemAttachFailed ? { oemAttachFailed: true } : undefined,
    });
  }, [scheduleContext, scheduleFields, addCreatedMaintenance, onCreated, handleClose, navigate]);

  // PDF import handler
  const handlePdfUpload = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setPdfError(t('workOrderImport.unsupportedFile'));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setPdfError(t('workOrderImport.fileTooLarge'));
      return;
    }

    setPdfUploading(true);
    setPdfError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/api/maintenances/create-from-pdf/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // CABAS: show preview instead of navigating
      if (response.data.document_type === 'cabas') {
        setCabasPreview(response.data.extracted_data);
        setPdfUploading(false);
        return;
      }

      // Örum: hand off immediately (existing behavior)
      await finalizeCreate(response.data);
    } catch (error) {
      const message = error.response?.data?.error || t('workOrderImport.error');
      setPdfError(message);
    } finally {
      setPdfUploading(false);
    }
  }, [t, finalizeCreate]);

  const handleCabasConfirm = useCallback(async () => {
    setCabasConfirming(true);
    setPdfError(null);
    try {
      const response = await axios.post('/api/maintenances/create-from-cabas/', {
        extracted_data: cabasPreview,
      });
      await finalizeCreate(response.data);
    } catch (error) {
      const message = error.response?.data?.error || t('workOrderImport.error');
      setPdfError(message);
      setCabasConfirming(false);
    }
  }, [cabasPreview, t, finalizeCreate]);

  // CABAS API import: the server created the work order; load it and run the shared tail.
  // An estimate imported before updates (or already is on) its own work order: open
  // that one instead of treating it as new.
  const handleCabasImported = useCallback(async (result) => {
    if (result.mode !== 'create') {
      handleClose();
      navigate(`/maintenances/${result.maintenance.id}`);
      return;
    }
    let maintenanceData;
    try {
      maintenanceData = (await axios.get(`/api/maintenances/${result.maintenance.id}/`)).data;
    } catch (error) {
      // The work order exists already; never let a failed reload look like a failed import.
      handleClose();
      navigate(`/maintenances/${result.maintenance.id}`);
      return;
    }
    await finalizeCreate(maintenanceData);
  }, [finalizeCreate, handleClose, navigate]);

  const handleCabasCancel = useCallback(() => {
    setCabasPreview(null);
    setCabasConfirming(false);
    setPdfError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handlePdfUpload,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: pdfUploading,
  });

  // Interrupt lookup and switch to manual entry
  const handleInterruptLookup = useCallback(() => {
    interruptLookup();
    setManualVehicleEntry(true);
  }, [interruptLookup]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleVehicleFormFieldChange = (e) => {
    const { name, value: rawValue } = e.target;

    // Strip whitespace (including line breaks) and force uppercase for registration number
    const value = name === 'registration_number'
      ? rawValue.replace(/\s+/g, '').trim().toUpperCase()
      : rawValue;

    // When registration number changes, clear all looked-up vehicle data
    // and let the lookup hook debounce the typeahead + free org-DB auto-search
    if (name === 'registration_number') {
      setFormData({
        ...formData,
        vehicle: {
          registration_number: value,
        },
      });
      setManualVehicleEntry(false);
      if (customerAutoFilledRef.current === 'auto') {
        setSelectedCustomer(null);
        customerAutoFilledRef.current = 'none';
      }
      handleRegistrationInput(value);
    } else {
      // Update other vehicle fields normally
      setFormData({
        ...formData,
        vehicle: {
          ...formData.vehicle,
          [name]: value,
        },
      });
    }
  };

  // Cleanup on unmount (the lookup hook cleans up its own timers/requests)
  useEffect(() => {
    return () => {
      if (odometerWarningTimeoutRef.current) {
        clearTimeout(odometerWarningTimeoutRef.current);
      }
    };
  }, []);

  // Show warning if odometer/hour meter reading is lower than previous (debounced 1 second)
  useEffect(() => {
    if (odometerWarningTimeoutRef.current) {
      clearTimeout(odometerWarningTimeoutRef.current);
    }

    setOdometerWarning(null);

    const warnings = [];
    const previousOdometer = formData.vehicle?.latest_odometer;
    if (odometerReading && previousOdometer) {
      const newReading = parseInt(odometerReading, 10);
      if (!isNaN(newReading) && newReading < previousOdometer) {
        warnings.push(t('odometer.warningLower', { new: newReading, previous: previousOdometer }));
      }
    }

    const previousHours = formData.vehicle?.latest_hour_meter;
    if (hourMeterReading && previousHours) {
      const newHours = parseInt(hourMeterReading, 10);
      if (!isNaN(newHours) && newHours < previousHours) {
        warnings.push(t('odometer.warningLowerHours', { new: newHours, previous: previousHours }));
      }
    }

    if (warnings.length > 0) {
      odometerWarningTimeoutRef.current = setTimeout(() => {
        setOdometerWarning(warnings.join('\n'));
      }, 1000);
    }

    return () => {
      if (odometerWarningTimeoutRef.current) {
        clearTimeout(odometerWarningTimeoutRef.current);
      }
    };
  }, [odometerReading, hourMeterReading, formData.vehicle?.latest_odometer, formData.vehicle?.latest_hour_meter, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);

    // Cancel any ongoing lookup
    cancelLookup();

    // A new customer with nothing to identify it by would be skipped below and its details lost: refuse
    if (customerMode === 'new' && isNewCustomerIncomplete(newCustomer)) {
      setSubmitError(t('customer.newIdentityMissing'));
      setIsSubmitting(false);
      return;
    }

    try {
      let customerId = selectedCustomer?.id || null;

      // Create new customer first if needed
      if (customerMode === 'new' && isNewCustomerValid(newCustomer)) {
        try {
          const customerResponse = await axios.post('/api/customers/', newCustomer);
          customerId = customerResponse.data.id;
        } catch (customerErr) {
          setSubmitError(flattenDrfError(customerErr, t('Failed to create customer')));
          setIsSubmitting(false);
          return;
        }
      }

      const payload = {
        ...formData,
        state: initialState,
        customer_id: customerId,
        // With a calendar slot the employee chip-grid owns the assignments;
        // the Mechanics autocomplete is hidden in that case.
        employee_ids: scheduleContext
          ? (scheduleFields?.employeeIds || [])
          : selectedEmployees.map(emp => emp.id),
      };
      const response = await axios.post('/api/maintenances/', payload);

      // Apply service package if selected
      if (selectedPackage && response.data?.id) {
        try {
          await axios.post(`/api/maintenances/${response.data.id}/apply-package/`, {
            package_id: selectedPackage.id,
          });
        } catch (pkgError) {
          // Non-blocking: work order was created successfully, package just failed to apply.
          // User can apply it manually from the work order view.
        }
      }

      // Attach the OEM service package picked in this dialog (UX-1). This is the
      // billable step the user explicitly chose — a failure must not be silent.
      let oemAttachFailed = false;
      if (oemSelection && response.data?.id) {
        try {
          await axios.post(`/api/oem/augment/${response.data.id}/`, {
            service_id: oemSelection.service_id,
            extra_service_ids: oemSelection.extra_service_ids,
            mileage: oemSelection.mileage,
            parts: oemSelection.parts,
            organization: currentOrganization?.organization_id,
          });
        } catch (oemError) {
          // Non-blocking: the work order exists (empty), so the process view shows
          // the "Lisää OEM-huolto" retry button — but tell the user what happened.
          console.error('Error attaching OEM package:', oemError);
          oemAttachFailed = true;
        }
      }

      // Record odometer/hour meter reading if provided
      if ((odometerReading || hourMeterReading) && response.data?.id) {
        try {
          const readingPayload = { context: 'intake' };
          if (odometerReading) readingPayload.reading_km = parseInt(odometerReading, 10);
          if (hourMeterReading) readingPayload.reading_hours = parseInt(hourMeterReading, 10);
          await axios.post(`/api/maintenances/${response.data.id}/record_odometer/`, readingPayload);
        } catch (odometerError) {
          console.error('Error recording odometer:', odometerError);
        }
      }

      setFormData({});
      setSelectedCustomer(null);
      customerAutoFilledRef.current = 'none';
      setSelectedEmployees([]);
      setOdometerReading('');
      setOemSelection(null);
      await finalizeCreate(response.data, { oemAttachFailed });
      setIsSubmitting(false);
    } catch (error) {
      console.error('There was an error adding the maintenance!', error);
      setSubmitError(flattenDrfError(error, t('Failed to save. Please try again.')));
      setIsSubmitting(false);
    }
  };

  const onClose = () => {
    setCreateMode('manual');
    setPdfUploading(false);
    setPdfError(null);
    setCabasPreview(null);
    setCabasConfirming(false);
    setFormData({});
    resetLookup();
    setManualVehicleEntry(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setSelectedCustomer(null);
    customerAutoFilledRef.current = 'none';
    setSelectedEmployees([]);
    setCustomerSearchInput('');
    setOdometerReading('');
    setSelectedPackage(null);
    setOemSelection(null);
    setOemPickerOpen(false);
    oemVehicleIdRef.current = null;
    setCustomerMode('existing');
    setNewCustomer({ ...EMPTY_NEW_CUSTOMER });
    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }
    handleClose();
  };

  // Determine if lookup button should be shown
  const regNumber = formData.vehicle?.registration_number || '';
  const showLookupButton = regNumber.length >= 3 && !vehicleLookedUp && !lookupLoading && !manualVehicleEntry;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={cabasPreview || createMode === 'cabas' ? 'md' : 'sm'}
      fullWidth
      fullScreen={fullScreen}
      data-testid="add-maintenance-dialog"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {initialState === 'quote' ? t('New quote') : t('New maintenance')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.disabled',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        {/* Mode Toggle - shown when PDF import and/or AI work order is enabled */}
        {(pdfImportEnabled || aiWorkOrderEnabled || cabasEnabled) && (
          <ToggleButtonGroup
            value={createMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setCreateMode(newMode);
                setPdfError(null);
                setSubmitError(null);
              }
            }}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            data-testid="create-mode-toggle"
          >
            <ToggleButton value="manual" sx={{ textTransform: 'none' }} data-testid="mode-manual">
              <CreateIcon sx={{ mr: 1, fontSize: 18 }} />
              {t('workOrderImport.manualEntry')}
            </ToggleButton>
            {pdfImportEnabled && (
              <ToggleButton value="pdf" sx={{ textTransform: 'none' }} data-testid="mode-pdf">
                <UploadFileIcon sx={{ mr: 1, fontSize: 18 }} />
                {t('workOrderImport.importFromPdf')}
              </ToggleButton>
            )}
            {aiWorkOrderEnabled && (
              <ToggleButton value="ai" sx={{ textTransform: 'none' }} data-testid="mode-ai">
                <AutoAwesomeOutlinedIcon sx={{ mr: 1, fontSize: 18 }} />
                {t('aiWorkOrder.tab')}
              </ToggleButton>
            )}
            {cabasEnabled && (
              <ToggleButton value="cabas" sx={{ textTransform: 'none' }} data-testid="mode-cabas">
                <RequestQuoteIcon sx={{ mr: 1, fontSize: 18 }} />
                {t('cabas.modeTab')}
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        )}

        {/* CABAS API import mode */}
        {createMode === 'cabas' && (
          <Box sx={{ py: 1 }}>
            <CabasEstimatePicker
              onImported={handleCabasImported}
              onOpenMaintenance={(maintenanceId) => {
                onClose();
                navigate(`/maintenances/${maintenanceId}`);
              }}
              onCancel={onClose}
            />
          </Box>
        )}

        {/* PDF Import Mode */}
        {createMode === 'pdf' && (
          <Box sx={{ py: 2 }}>
            {cabasPreview ? (
              <CabasPreview
                data={cabasPreview}
                onConfirm={handleCabasConfirm}
                onCancel={handleCabasCancel}
                confirming={cabasConfirming}
                error={pdfError}
              />
            ) : pdfUploading ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 6,
                  gap: 2,
                }}
              >
                <CircularProgress size={48} />
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'text.primary',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {t('workOrderImport.extracting')}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('workOrderImport.extractingDescription')}
                </Typography>
              </Box>
            ) : (
              <>
                <Collapse in={!!pdfError}>
                  <Alert
                    severity="error"
                    sx={{ mb: 2, borderRadius: '10px' }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setPdfError(null)}
                      >
                        {t('workOrderImport.tryAgain')}
                      </Button>
                    }
                  >
                    {pdfError}
                  </Alert>
                </Collapse>
                <Box
                  {...getRootProps()}
                  data-testid="pdf-dropzone"
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    borderRadius: '12px',
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'background.default',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: isDragActive ? 'primary.main' : 'text.secondary',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {isDragActive
                      ? t('workOrderImport.dropzoneActive')
                      : t('workOrderImport.dropzone')
                    }
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* AI Work Order Mode: pick a vehicle + describe the work, then generate a suggestion */}
        {createMode === 'ai' && (
          <Box sx={{ py: 1 }} data-testid="ai-work-order-form">
            <Alert severity="info" icon={<AutoAwesomeOutlinedIcon />} sx={{ mb: 2, borderRadius: '10px' }}>
              {t('aiWorkOrder.intro')}
            </Alert>

            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 0.5 }}>
              {t('Registration Number')} *
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                name="registration_number"
                placeholder="ABC-123"
                value={formData.vehicle?.registration_number || ''}
                onChange={(e) => {
                  handleVehicleFormFieldChange(e);
                  if (vehicleLookedUp) setVehicleLookedUp(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLookupClick();
                  }
                }}
                InputProps={{ sx: { ...plateFont } }}
                data-testid="ai-registration-input"
              />
              <Button
                variant="outlined"
                onClick={handleLookupClick}
                disabled={lookupLoading || (formData.vehicle?.registration_number || '').length < 3}
                startIcon={lookupLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ whiteSpace: 'nowrap', borderRadius: '10px' }}
              >
                {t('vehicleLookup.button')}
              </Button>
            </Box>
            {lookupError && (
              <Alert severity="error" sx={{ mt: 1, borderRadius: '10px' }}>{lookupError}</Alert>
            )}
            {vehicleLookedUp && formData.vehicle && (
              <Box sx={{ mt: 1, p: 1.5, borderRadius: '8px', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <DirectionsCarIcon sx={{ color: 'secondary.main' }} />
                <Typography sx={{ fontWeight: 600 }}>
                  {[formData.vehicle.manufacturer, formData.vehicle.model].filter(Boolean).join(' ')}
                  {formData.vehicle.year_model ? ` (${formData.vehicle.year_model})` : ''}
                </Typography>
                {!formData.vehicle.id && (
                  <Chip size="small" color="warning" label={t('aiWorkOrder.notInSystem')} />
                )}
              </Box>
            )}
            {formData.vehicle?.id && (
              <InternalNotesAlert vehicleNote={formData.vehicle.internal_note} sx={{ mt: 1 }} />
            )}

            <TextField
              sx={{ mt: 2 }}
              fullWidth
              multiline
              minRows={3}
              label={t('aiWorkOrder.requestLabel')}
              placeholder={t('aiWorkOrder.requestPlaceholder')}
              value={aiRequestText}
              onChange={(e) => setAiRequestText(e.target.value)}
              data-testid="ai-request-text"
            />
          </Box>
        )}

        {/* Manual Entry Mode */}
        {createMode === 'manual' && (
        <Box component="form" id="add-maintenance-form" onSubmit={handleSubmit}>
          {/* Submit Error */}
          <Collapse in={!!submitError}>
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: '10px' }}
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          </Collapse>

          {/* Registration Number */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'text.primary',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('Registration Number')} *
              </Typography>
              <HelpButton text={t('registrationNumberHelp')} size={16} />
            </Box>
            <Autocomplete
              freeSolo
              open={vehicleTypeaheadOptions.length > 0 && !vehicleLookedUp}
              options={vehicleTypeaheadOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : (option.registration_number || '')}
              filterOptions={(x) => x}
              loading={vehicleTypeaheadLoading}
              onOpen={() => { vehicleTypeaheadOpenRef.current = true; }}
              onClose={() => { vehicleTypeaheadOpenRef.current = false; }}
              inputValue={formData.vehicle?.registration_number || ''}
              onInputChange={(event, newValue, reason) => {
                if (reason === 'input') {
                  handleVehicleFormFieldChange({ target: { name: 'registration_number', value: newValue } });
                } else if (reason === 'clear') {
                  handleVehicleFormFieldChange({ target: { name: 'registration_number', value: '' } });
                }
              }}
              onChange={(event, newValue, reason) => {
                if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
                  handleVehicleTypeaheadSelect(newValue);
                }
              }}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={option.id} {...restProps}>
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, ...plateFont }}
                      >
                        {option.registration_number}
                        {(option.manufacturer || option.model) && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1, fontFamily: 'inherit', fontWeight: 400 }}
                          >
                            {[option.manufacturer, option.model].filter(Boolean).join(' ')}
                            {option.year_model ? ` (${option.year_model})` : ''}
                          </Typography>
                        )}
                      </Typography>
                      {option.last_customer_name && (
                        <Typography variant="caption" color="text.secondary">
                          {option.last_customer_name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                  required
                  data-testid="registration-input"
                  name="registration_number"
                  placeholder="ABC-123"
                  variant="outlined"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showLookupButton && !vehicleTypeaheadOpenRef.current) {
                        handleLookupClick();
                      }
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      ...plateFont,
                      fontSize: '14px',
                    },
                    endAdornment: (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {lookupLoading && (
                            <>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              <Button
                                size="small"
                                onClick={handleInterruptLookup}
                                sx={{
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.5,
                                  fontSize: '12px',
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'text.primary',
                                    bgcolor: 'action.hover',
                                  },
                                }}
                              >
                                {t('Enter manually')}
                              </Button>
                            </>
                          )}
                          {showLookupButton && (
                            <Button
                              size="small"
                              onClick={handleLookupClick}
                              startIcon={<SearchIcon sx={{ fontSize: '16px !important' }} />}
                              sx={{
                                minWidth: 'auto',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t('vehicleLookup.button')}
                            </Button>
                          )}
                          {vehicleLookedUp && !lookupLoading && (
                            <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                          )}
                        </Box>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                />
              )}
            />
            {/* Quota indicator + manual entry link */}
            {!vehicleLookedUp && !manualVehicleEntry && regNumber.length >= 3 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 1, flexWrap: 'wrap' }}>
                {quotaInfo && quotaInfo.vehicle_data_provider === 'rekisterinumeroapi' ? (
                  <>
                    <Typography variant="caption" color="success.main">
                      {t('vehicleLookup.fallbackProviderShort')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">|</Typography>
                  </>
                ) : quotaInfo && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {t('vehicleLookup.quotaRemaining', {
                        remaining: Math.max(0, quotaInfo.remaining_lookups),
                        total: quotaInfo.included_lookups,
                      })}
                    </Typography>
                    <HelpButton text={t('vehicleLookup.quotaHelp')} size={16} />
                    <Typography variant="caption" color="text.secondary">|</Typography>
                  </>
                )}
                <Typography
                  variant="caption"
                  color="secondary.main"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setManualVehicleEntry(true)}
                >
                  {t('vehicleLookup.enterManually')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Lookup Error - with option to enter manually */}
          <Collapse in={!!lookupError && !manualVehicleEntry}>
            <Alert
              severity="warning"
              sx={{ mb: 2, borderRadius: '10px' }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setManualVehicleEntry(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Enter manually')}
                </Button>
              }
            >
              {lookupError}
            </Alert>
          </Collapse>

          {/* Trial Info Box - vehicle lookup quota exceeded or paid feature */}
          <Collapse in={showTrialInfo}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{
                mb: 2,
                borderRadius: '10px',
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {t('Vehicle lookup is a paid feature')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('vehicleLookup.trialMessage')}
              </Typography>
            </Alert>
          </Collapse>

          {/* Non-Finnish registration info - offer manual entry */}
          <Collapse in={showNonFinnishInfo && !manualVehicleEntry}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{ mb: 2, borderRadius: '10px' }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setManualVehicleEntry(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Enter manually')}
                </Button>
              }
            >
              {t('Vehicle lookup is only available for Finnish registration numbers (e.g. ABC-123). You can enter vehicle details manually if needed.')}
            </Alert>
          </Collapse>

          {/* Vehicle Info (shown after successful lookup, not in manual entry mode) */}
          <Collapse in={vehicleLookedUp && !!formData.vehicle?.manufacturer && !manualVehicleEntry}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'text.primary',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {t('Vehicle Information')}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setManualVehicleEntry(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formData.vehicle?.manufacturer} {formData.vehicle?.model}
                  </Typography>
                  {formData.vehicle?.model_variant && (
                    <Typography variant="caption" color="text.secondary">
                      {formData.vehicle?.model_variant}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {formData.vehicle?.year_model && (
                      <Chip
                        size="small"
                        label={`${formData.vehicle.year_model}`}
                        variant="outlined"
                      />
                    )}
                    {formData.vehicle?.fuel && (
                      <Chip
                        size="small"
                        label={formData.vehicle.fuel}
                        variant="outlined"
                      />
                    )}
                    {formData.vehicle?.power_kw && (
                      <Chip
                        size="small"
                        label={`${formData.vehicle.power_kw} kW`}
                        variant="outlined"
                      />
                    )}
                    {formData.vehicle?.displacement_cc && (
                      <Chip
                        size="small"
                        label={`${formData.vehicle.displacement_cc} cc`}
                        variant="outlined"
                      />
                    )}
                    {formData.vehicle?.engine_code && (
                      <Chip
                        size="small"
                        label={formData.vehicle.engine_code}
                        variant="outlined"
                      />
                    )}
                    {(formData.vehicle?.latest_odometer || formData.vehicle?.latest_hour_meter) && (
                      <Chip
                        size="small"
                        icon={<SpeedIcon sx={{ fontSize: '14px !important' }} />}
                        label={[
                          formData.vehicle.latest_odometer && `${formData.vehicle.latest_odometer.toLocaleString('fi-FI')} km`,
                          formData.vehicle.latest_hour_meter && `${formData.vehicle.latest_hour_meter.toLocaleString('fi-FI')} h`,
                        ].filter(Boolean).join(' / ')}
                        variant="outlined"
                        color="primary"
                      />
                    )}
                  </Box>
                </Grid>
                {formData.vehicle?.vin && (
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        ...numFont,
                        color: 'text.secondary',
                      }}
                    >
                      VIN: {formData.vehicle.vin}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>

          {/* The found vehicle's internal note (only a vehicle already in the register has one) */}
          {formData.vehicle?.id && (
            <InternalNotesAlert vehicleNote={formData.vehicle.internal_note} sx={{ mb: 3 }} />
          )}

          {/* OEM Service Suggestion Banner — opens the in-dialog package picker (UX-1) */}
          <Collapse in={vehicleLookedUp && !!formData.vehicle?.tecdoc_id && oemServiceEnabled && !oemSelection}>
            <Box
              onClick={() => setOemPickerOpen(true)}
              sx={{
                mb: 3,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'rgba(49, 130, 206, 0.3)',
                bgcolor: 'rgba(49, 130, 206, 0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(49, 130, 206, 0.08)',
                  borderColor: 'secondary.main',
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  bgcolor: 'rgba(49, 130, 206, 0.10)',
                  color: 'secondary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {t('oem.suggestionTitle')}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  {t('oem.suggestionDescription')}
                  {oemChargeStatus?.already_charged ? (
                    <Typography component="span" sx={{ fontSize: 'inherit', color: '#2f8c54', fontWeight: 600 }}>
                      {' '}{t('oem.suggestionAlreadyPaid')}
                    </Typography>
                  ) : oemChargeStatus?.price_eur ? (
                    ` ${t('oem.suggestionPriceNote', {
                      price: formatPriceWithCurrency(oemChargeStatus.price_eur),
                    })}`
                  ) : currentOrganization?.oem_lookup_price ? (
                    ` ${t('oem.suggestionPriceNote', {
                      price: formatPriceWithCurrency(currentOrganization.oem_lookup_price),
                    })}`
                  ) : ''}
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ fontSize: 18, color: 'secondary.main', flexShrink: 0 }} />
            </Box>
          </Collapse>

          {/* Selected OEM package chip */}
          <Collapse in={!!oemSelection}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'rgba(56, 161, 105, 0.4)',
                bgcolor: 'rgba(56, 161, 105, 0.06)',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: '#38a169', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>
                  {t('oem.packageSelected')}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }} noWrap>
                  {oemSelection?.summary?.name ? `${oemSelection.summary.name} · ` : ''}
                  {oemSelection?.summary?.km ? `${oemSelection.summary.km.toLocaleString('fi-FI')} km · ` : ''}
                  {oemSelection?.summary?.parts_count ?? 0} {t('oem.parts')}
                  {oemSelection?.summary?.extras_count ? ` · +${oemSelection.summary.extras_count}` : ''}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label={t('oem.removeSelection')}
                onClick={() => setOemSelection(null)}
                sx={{ flexShrink: 0 }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Collapse>

          {/* Manual Vehicle Entry Fields */}
          <Collapse in={manualVehicleEntry}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'text.primary',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {t('Vehicle Information')}
                  </Typography>
                </Box>
                {vehicleLookedUp && (
                  <Button
                    size="small"
                    onClick={() => setManualVehicleEntry(false)}
                    sx={{ color: 'text.secondary' }}
                  >
                    {t('Cancel')}
                  </Button>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    name="manufacturer"
                    label={t('Manufacturer')}
                    placeholder={t('e.g. Toyota')}
                    value={formData.vehicle?.manufacturer || ''}
                    onChange={handleVehicleFormFieldChange}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="model"
                    label={t('Model')}
                    placeholder={t('e.g. Corolla')}
                    value={formData.vehicle?.model || ''}
                    onChange={handleVehicleFormFieldChange}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="year_model"
                    label={t('Year')}
                    placeholder={t('e.g. 2020')}
                    value={formData.vehicle?.year_model || ''}
                    onChange={handleVehicleFormFieldChange}
                    fullWidth
                    size="small"
                    type="number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="vin"
                    label={t('VIN')}
                    placeholder={t('e.g. WVWZZZ3CZWE123456')}
                    value={formData.vehicle?.vin || ''}
                    onChange={handleVehicleFormFieldChange}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                    inputProps={{
                      style: { ...numFont },
                      maxLength: 17,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {t('Description')} ({t('optional')})
            </Typography>
            <TextField
              data-testid="description-input"
              name="description"
              value={formData.description || ''}
              placeholder={t('E.g. Oil change, brake inspection...')}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          </Box>

          {/* Scheduling (only when opened with a calendar slot context) */}
          {scheduleContext && scheduleFields && (
            <Box sx={{ mb: 3 }}>
              <ScheduleSlotFields
                t={t}
                value={scheduleFields}
                onChange={setScheduleFields}
                resources={schedulingResources}
              />
            </Box>
          )}

          {/* Service Package */}
          {servicePackages.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {t('huoltopaketit.navLabel')} ({t('optional')})
              </Typography>
              <Autocomplete
                options={servicePackages}
                getOptionLabel={(option) => option?.name || ''}
                value={selectedPackage}
                onChange={(e, value) => {
                  setSelectedPackage(value);
                  if (value?.description && !formData.description) {
                    setFormData((prev) => ({ ...prev, description: value.description }));
                  }
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.job_count || 0} {t('huoltopaketit.jobsShort')} / {option.part_count || 0} {t('huoltopaketit.partsShort')}
                        {option.total_hours ? ` / ${option.total_hours} h` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    placeholder={t('huoltopaketit.searchPlaceholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* Odometer / Hour Meter Reading */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <SpeedIcon sx={{ fontSize: 18 }} />
              {t('odometer.reading')} ({t('optional')})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                name="odometer_reading"
                placeholder={t('odometer.placeholder')}
                value={odometerReading}
                onChange={(e) => setOdometerReading(e.target.value)}
                fullWidth
                type="number"
                variant="outlined"
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: <Typography color="text.secondary" sx={{ ml: 1 }}>km</Typography>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <TextField
                name="hour_meter_reading"
                placeholder={t('odometer.placeholderHours')}
                value={hourMeterReading}
                onChange={(e) => setHourMeterReading(e.target.value)}
                fullWidth
                type="number"
                variant="outlined"
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: <Typography color="text.secondary" sx={{ ml: 1 }}>h</Typography>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
            </Box>
            {odometerWarning && (
              <Alert
                severity="warning"
                sx={{
                  mt: 1,
                  borderRadius: '10px',
                  '& .MuiAlert-message': {
                    fontSize: '13px',
                    whiteSpace: 'pre-line',
                  },
                }}
              >
                {odometerWarning}
              </Alert>
            )}
          </Box>

          {/* Customer */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {t('Customer')} ({t('optional')})
            </Typography>

            {/* Customer Mode Toggle */}
            <ToggleButtonGroup
              value={customerMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) {
                  setCustomerMode(newMode);
                  setSubmitError(null);
                }
              }}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            >
              <ToggleButton value="existing" sx={{ textTransform: 'none' }}>
                <PersonSearchIcon sx={{ mr: 1 }} />
                {t('Existing customer')}
              </ToggleButton>
              <ToggleButton value="new" sx={{ textTransform: 'none' }}>
                <AddIcon sx={{ mr: 1 }} />
                {t('New customer')}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Existing Customer Selection */}
            {customerMode === 'existing' && (
              <Autocomplete
                data-testid="customer-autocomplete"
                options={customers}
                getOptionLabel={(option) => customerLabel(option)}
                loading={customerSearchLoading}
                value={selectedCustomer}
                onChange={(e, value) => { setSelectedCustomer(value); customerAutoFilledRef.current = value ? 'manual' : 'none'; }}
                filterOptions={(x) => x}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') {
                    setCustomerSearchInput(value);
                  }
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {option.type === 'company' ? (
                          <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        ) : (
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {customerLabel(option)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        {option.email && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <EmailIcon sx={{ fontSize: 12 }} /> {option.email}
                          </Typography>
                        )}
                        {option.phone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <PhoneIcon sx={{ fontSize: 12 }} /> {option.phone}
                          </Typography>
                        )}
                        {option.vat_number && (
                          <Typography variant="caption" color="text.secondary">
                            {option.vat_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoComplete="off"
                    placeholder={t('Search by name, email, phone, or business ID...')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                      },
                    }}
                  />
                )}
              />
            )}

            {/* New Customer Form */}
            {customerMode === 'new' && (
              <NewCustomerForm t={t} value={newCustomer} onChange={setNewCustomer} />
            )}
          </Box>

          {/* Selected Customer Info */}
          <Collapse in={!!selectedCustomer && customerMode === 'existing'}>
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                {selectedCustomer?.type === 'company' ? (
                  <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                ) : (
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                )}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'text.primary',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {selectedCustomer?.name}
                </Typography>
                {selectedCustomer?.type === 'company' && (
                  <Chip
                    size="small"
                    label={t('Company')}
                    sx={{ ml: 1, height: 20, fontSize: '11px' }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {selectedCustomer?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedCustomer.email}
                    </Typography>
                  </Box>
                )}
                {selectedCustomer?.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedCustomer.phone}
                    </Typography>
                  </Box>
                )}
                {selectedCustomer?.address && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedCustomer.address}
                  </Typography>
                )}
                {selectedCustomer?.vat_number && (
                  <Typography
                    variant="caption"
                    sx={{
                      ...numFont,
                      color: 'text.secondary',
                      mt: 0.5,
                    }}
                  >
                    {t('VAT')}: {selectedCustomer.vat_number}
                  </Typography>
                )}
              </Box>
              {/* Customer's existing vehicles - shown only before a registration is entered */}
              {!formData.vehicle?.registration_number && (
                <CustomerVehicleChips
                  customerId={selectedCustomer?.id}
                  onSelect={handleVehicleTypeaheadSelect}
                />
              )}
            </Box>
          </Collapse>
          {customerMode === 'existing' && selectedCustomer && (
            <InternalNotesAlert customerNote={selectedCustomer.internal_note} sx={{ mb: 3 }} />
          )}

          {/* Mechanics (optional) — hidden with a calendar slot context, where the
              scheduling section's employee chip-grid owns the assignments */}
          {!scheduleContext && (
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'text.primary',
                mb: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {t('Mechanics')} ({t('optional')})
            </Typography>
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(option) => option?.name || ''}
              value={selectedEmployees}
              onChange={(e, value) => setSelectedEmployees(value)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                  placeholder={selectedEmployees.length === 0 ? t('Select mechanics') : ''}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                  />
                ))
              }
            />
          </Box>
          )}

        </Box>
        )}

      </DialogContent>

      {createMode === 'manual' && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': {
                borderColor: 'divider',
                bgcolor: 'background.default',
              },
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            form="add-maintenance-form"
            variant="contained"
            disabled={isSubmitting}
            data-testid="submit-maintenance"
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: '10px',
              bgcolor: 'secondary.main',
              color: 'white',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
              '&:hover': {
                bgcolor: 'secondary.light',
                boxShadow: '0 6px 16px rgba(49, 130, 206, 0.4)',
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : initialState === 'quote' ? (
              <span>{t('Create quote')}</span>
            ) : scheduleContext ? (
              <span>{t('Create and schedule')}</span>
            ) : (
              <span>{t('Create maintenance')}</span>
            )}
          </Button>
        </DialogActions>
      )}

      {createMode === 'ai' && (
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ px: 3, py: 1.25, borderRadius: '10px', borderColor: 'divider', color: 'text.primary', fontWeight: 500 }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            data-testid="ai-generate"
            disabled={
              !aiRequestText.trim() ||
              !formData.vehicle?.registration_number ||
              !(formData.vehicle?.id || formData.vehicle?.manufacturer)
            }
            onClick={() => {
              if (onSwitchToAi) {
                onSwitchToAi({ vehicle: formData.vehicle, customer: selectedCustomer, text: aiRequestText.trim() });
              }
            }}
            startIcon={<AutoAwesomeOutlinedIcon />}
            sx={{
              px: 3, py: 1.25, borderRadius: '10px', bgcolor: 'secondary.main', color: 'white', fontWeight: 500,
              boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {t('aiWorkOrder.generate')}
          </Button>
        </DialogActions>
      )}

      {/* Extra Lookup Confirmation Dialog */}
      <ExtraLookupConfirmDialog
        open={extraLookupDialogOpen}
        onClose={() => setExtraLookupDialogOpen(false)}
        onConfirm={() => confirmExtraLookup()}
        quotaInfo={quotaInfo}
        isTrial={quotaInfo?.is_trial || false}
      />

      {/* OEM package picker (UX-1): selection is attached to the work order on save */}
      {oemServiceEnabled && (
        <OemServiceDialog
          open={oemPickerOpen}
          handleClose={() => setOemPickerOpen(false)}
          mode="picker"
          vehicleData={formData.vehicle}
          initialMileage={
            odometerReading
              ? parseInt(odometerReading, 10)
              : formData.vehicle?.latest_odometer || undefined
          }
          onPackageSelected={(selection) => setOemSelection(selection)}
        />
      )}
    </Dialog>
  );
}

export default withTranslation()(AddMaintenanceDialog);
